/**
 * Construction Dashboard - Phase 2
 * Uses industry_projects table with polymorphic design
 */

import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Briefcase, TrendingUp, CheckCircle, AlertTriangle } from 'lucide-react';
import { CompanyType } from '@/types/company';
import { useIndustryProjects } from '@/hooks/industry';
import { MetricCard } from '@/components/industry/shared/MetricCard';
import { StatusBadge } from '@/components/industry/shared/StatusBadge';
import { isConstructionProject } from '@/types/industry';
import { formatCurrency } from '@/lib/utils/currencyUtils';

export function ConstructionDashboard() {
  const { projects, isLoading, error } = useIndustryProjects({ domain: CompanyType.CONSTRUCTION });

  // Calculate metrics from projects data
  const metrics = useMemo(() => {
    if (!projects || projects.length === 0) {
      return {
        worksInProgress: 0,
        averageProfitability: 0,
        completedThisMonth: 0,
        worksOverBudget: [],
        totalRevenue: 0,
        totalCosts: 0,
        totalProfit: 0,
        profitMarginAvg: 0,
      };
    }

    // Works in progress (active status)
    const worksInProgress = projects.filter((p) => p.status === 'active').length;

    // Calculate profitability for each project
    const projectsWithProfit = projects
      .filter((p) => p.budget_revenue && p.budget_revenue > 0)
      .map((p) => {
        const revenue = p.actual_revenue || 0;
        const cost = p.actual_cost || 0;
        const profit = revenue - cost;
        const profitMargin = revenue > 0 ? (profit / revenue) * 100 : 0;
        return { ...p, profit, profitMargin };
      });

    const averageProfitability =
      projectsWithProfit.length > 0
        ? projectsWithProfit.reduce((sum, p) => sum + p.profitMargin, 0) / projectsWithProfit.length
        : 0;

    // Completed this month
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const completedThisMonth = projects.filter((p) => {
      if (!p.completed_date) return false;
      const completedDate = new Date(p.completed_date);
      return (
        completedDate.getMonth() === currentMonth &&
        completedDate.getFullYear() === currentYear
      );
    }).length;

    // Works over budget (actual_cost > budget_cost)
    const worksOverBudget = projects.filter((p) => {
      if (!p.budget_cost || p.budget_cost === 0) return false;
      return p.actual_cost > p.budget_cost;
    }).map((p) => {
      const variance = p.actual_cost - (p.budget_cost || 0);
      const variancePercentage = p.budget_cost ? (variance / p.budget_cost) * 100 : 0;
      return {
        ...p,
        variance,
        variancePercentage,
      };
    });

    // Financial totals
    const totalRevenue = projects.reduce((sum, p) => sum + (p.actual_revenue || 0), 0);
    const totalCosts = projects.reduce((sum, p) => sum + (p.actual_cost || 0), 0);
    const totalProfit = totalRevenue - totalCosts;
    const profitMarginAvg = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

    return {
      worksInProgress,
      averageProfitability,
      completedThisMonth,
      worksOverBudget,
      totalRevenue,
      totalCosts,
      totalProfit,
      profitMarginAvg,
    };
  }, [projects]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Carregando dashboard de obras...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-destructive">Erro ao carregar dashboard</div>
      </div>
    );
  }


  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Dashboard de Obras</h1>
        <p className="text-muted-foreground">
          Visão geral do desempenho das suas obras
        </p>
      </div>

      {/* Metric Cards - Using new MetricCard component */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Obras em Andamento"
          value={metrics.worksInProgress}
          icon={Briefcase}
          iconClassName="bg-blue-100 dark:bg-blue-900"
        />

        <MetricCard
          title="Lucratividade Média"
          value={`${metrics.averageProfitability.toFixed(1)}%`}
          icon={TrendingUp}
          iconClassName="bg-green-100 dark:bg-green-900"
        />

        <MetricCard
          title="Concluídas Este Mês"
          value={metrics.completedThisMonth}
          icon={CheckCircle}
          iconClassName="bg-emerald-100 dark:bg-emerald-900"
        />

        <MetricCard
          title="Acima do Orçamento"
          value={metrics.worksOverBudget.length}
          icon={AlertTriangle}
          iconClassName="bg-red-100 dark:bg-red-900"
        />
      </div>

      {/* Over Budget Alert */}
      {metrics.worksOverBudget.length > 0 && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Atenção: Obras Acima do Orçamento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {metrics.worksOverBudget.map((work) => (
                <div
                  key={work.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex-1">
                    <div className="font-medium">{work.name}</div>
                    {work.description && (
                      <div className="text-sm text-muted-foreground">
                        {work.description}
                      </div>
                    )}
                    <div className="mt-1">
                      <StatusBadge status={work.status} />
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <div className="text-sm font-medium text-destructive">
                      {formatCurrency(work.variance)} acima
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {Math.abs(work.variancePercentage).toFixed(1)}% sobre o orçado
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Financial Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Receita Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {formatCurrency(metrics.totalRevenue)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Custos Totais</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {formatCurrency(metrics.totalCosts)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Lucro Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {formatCurrency(metrics.totalProfit)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Margem: {metrics.profitMarginAvg.toFixed(1)}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Projects */}
      {projects && projects.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Obras Recentes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {projects.slice(0, 5).map((project) => (
                <div
                  key={project.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent transition-colors"
                >
                  <div className="flex-1">
                    <div className="font-medium">{project.name}</div>
                    {project.description && (
                      <div className="text-sm text-muted-foreground">
                        {project.description}
                      </div>
                    )}
                    {isConstructionProject(project) && project.metadata.location && (
                      <div className="text-xs text-muted-foreground mt-1">
                        📍 {project.metadata.location}
                      </div>
                    )}
                    <div className="mt-1">
                      <StatusBadge status={project.status} />
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    {project.budget_revenue && (
                      <div className="text-sm font-medium">
                        {formatCurrency(project.budget_revenue)}
                      </div>
                    )}
                    {project.actual_cost > 0 && (
                      <div className="text-xs text-muted-foreground">
                        Custo: {formatCurrency(project.actual_cost)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {projects && projects.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Briefcase className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhuma obra cadastrada</h3>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              Comece cadastrando sua primeira obra para acompanhar custos, receitas e lucratividade.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
